import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useMutation, useQuery } from "@apollo/client";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";
import { notify } from "../../../feedback/SnackbarProvider";
import { Alert } from "../../Alert/Alert";
import { Button } from "../../Button/Button";
import { Input } from "../../Input/Input";
import { List } from "../../List/List";
import { Popconfirm } from "../../Overlay/Overlay";
import { Tabs } from "../../Tabs/Tabs";
import { Tag } from "../../Tag/Tag";
import { Tooltip } from "../../Tooltip/Tooltip";
import { Typography } from "../../Typography/Typography";
import { Modal } from "../../Modal/Modal";
import { Form } from "../../Form/Form";
import { Select } from "../../Select/Select";
import { Switch } from "../../Switch/Switch";
import {
  AccountTreeOutlined as BranchesOutlined,
  AllInclusive,
  AutoAwesomeOutlined,
  DeleteOutline as DeleteOutlined,
  EditOutlined,
  GroupsOutlined,
  HandymanOutlined,
  History as HistoryOutlined,
  Add as PlusOutlined,
  PersonOutline,
  PsychologyOutlined,
  PublicOutlined,
} from "@mui/icons-material";
import { useOptionalCurrentUser } from "../../../utils/hooks";
import { toolLabel, useAgentAssignableToolGroups } from "../availableTools";
import ButtonBase from "@mui/material/ButtonBase";
import { AGENT_PALETTE, speakerTint } from "../../ChatBox/speakerTint";
import { agentDisplayName } from "../../ChatBox/agentName";
import {
  ChatAgentRunMode,
  ChatEffort,
  ChatSkillScope,
  ChatToolType,
  type ChatAgentCapabilitiesQuery,
  type McpToolBundleType,
} from "_graphql-types-frontend/graphql";
import {
  effortLabel,
  MY_SPAWNABLE_CHAT_AGENTS,
  CHAT_AGENTS_BY_SCOPE,
  CREATE_CHAT_AGENT,
  UPDATE_CHAT_AGENT,
  DELETE_CHAT_AGENT,
  CHAT_AGENT_HISTORY,
  CHAT_AGENT_CAPABILITIES,
  MY_ACTIVE_CHAT_SKILLS,
  LLM_MODELS,
  BENCHMARK_SOURCE_URL,
  modelStats,
} from "../graphql";

const { Text } = Typography;

/**
 * What a NEW agent starts with selected.
 *
 * A sensible working set rather than "inherit everything": retrieval over
 * documents and notes, the platform's own data via MCP, and Atlas for
 * computation. Bounded-by-default is also the safer default — an author who
 * wants the spawning chat's whole toolkit can clear the selection, which is a
 * deliberate act rather than an oversight.
 */
/**
 * The card a rich tooltip has to paint for itself.
 *
 * The shared Tooltip removes MUI's dark surface whenever the title is not a
 * plain string, so a ReactNode tooltip renders transparent unless it supplies
 * one. Values match MUI's default tooltip so rich and string tooltips in this
 * file look like the same component.
 */
/** The muted one-liner above a settings tab, matching ToolsTab's. */
const TAB_INTRO_STYLE: CSSProperties = {
  fontSize: 12,
  color: "#666",
  marginBottom: 8,
  lineHeight: "17px",
};

const TOOLTIP_SURFACE: CSSProperties = {
  backgroundColor: "rgba(97, 97, 97, 0.92)",
  color: "#fff",
  padding: "6px 10px",
  borderRadius: 4,
  maxWidth: 420,
  fontSize: 12,
};

/**
 * Scope as an icon rather than a word.
 *
 * Three scopes repeated on every row is a lot of text for something the reader
 * mostly scans, and the words ("personal"/"organization"/"system") were the
 * longest tag in the row. The tooltip keeps it readable for anyone who needs
 * the exact term, and the icons escalate visually the way the scopes do:
 * one person -> a group -> everyone.
 */
const SCOPE_ICON: Record<
  string,
  { Icon: typeof PersonOutline; label: string }
> = {
  [ChatSkillScope.Personal]: {
    Icon: PersonOutline,
    label: "Personal — only you",
  },
  [ChatSkillScope.Organization]: {
    Icon: GroupsOutlined,
    label: "Organization — everyone in your organization",
  },
  [ChatSkillScope.System]: {
    Icon: PublicOutlined,
    label: "System — everyone on the platform",
  },
};

const ScopeIcon = ({ scope }: { scope: ChatSkillScope }) => {
  const entry = SCOPE_ICON[scope];
  if (!entry) return null;
  const { Icon, label } = entry;
  return (
    <Tooltip title={label}>
      <span
        data-testid={`agent-scope-${scope}`}
        style={{ display: "inline-flex" }}
      >
        <Icon style={{ fontSize: 14, color: "#888" }} />
      </span>
    </Tooltip>
  );
};

/**
 * Whether a model key takes an effort setting, from the server's registry.
 * An unpicked model (null) inherits the default, which the server gates itself.
 */
function useModelSupportsEffort(): (key: string | null | undefined) => boolean {
  const { data } = useQuery(LLM_MODELS, { fetchPolicy: "cache-first" });
  const models = data?.llmModels ?? [];
  return key =>
    key == null ||
    models.some(m => m.key === key && m.enabled && m.supportsEffort);
}

/** Presentation only: the allowed levels come from the server. */
const EFFORT_BLURB: Partial<Record<ChatEffort, string>> = {
  [ChatEffort.Low]: "quick lookups and retrieval",
  [ChatEffort.Medium]: "most analysis",
  [ChatEffort.High]: "adversarial review, multi-step reasoning",
};

function extractGqlMessage(error: unknown): string {
  const e = error as {
    graphQLErrors?: Array<{ message?: string }>;
    message?: string;
  } | null;
  return (
    e?.graphQLErrors?.[0]?.message ??
    e?.message ??
    "An unexpected error occurred."
  );
}

// Use the GENERATED enums, not hand-written strings. GraphQL serialises an
// enum by its NAME ("PERSISTENT"), while the database column stores the value
// ("persistent") — so hardcoding the lowercase form here produced
// `Value "persistent" does not exist in "ChatAgentRunMode" enum` on every
// save. SkillsTab has always imported ChatSkillScope for exactly this reason.
const SCOPE = ChatSkillScope;
const RUN_MODE = ChatAgentRunMode;

type AgentSkill = { id: number; name: string };

type ChatAgent = {
  id: number;
  name: string;
  description: string;
  directive: string;
  tools: string[] | null;
  model: string | null;
  effort: string | null;
  color: string | null;
  isAutoInvocable: boolean;
  defaultMode: ChatAgentRunMode;
  scope: ChatSkillScope;
  organizationId: number | null;
  version: number;
  createUser: string;
  createUserFullName: string | null;
  /** Server-computed. Deliberately NOT re-derived from scope/createUser: the
   *  UI must not drift from what ChatAgent.writeRule enforces. */
  canEdit: boolean;
  canDelete: boolean;
  skills: AgentSkill[];
};

type AgentHistoryEntry = {
  id: number;
  version: number;
  name: string;
  description: string;
  directive: string;
  tools: string[] | null;
  model: string | null;
  effort: string | null;
  defaultMode: ChatAgentRunMode;
  scope: ChatSkillScope;
  skills: Array<{ skillId: number; version: number }> | null;
  archivedAt: string;
  archivedBy: string;
};

type AgentFormValues = {
  name: string;
  description: string;
  directive: string;
  isAutoInvocable: boolean;
  model?: string;
  effort?: string;
  /** A palette key; null = automatic. Held outside Form.Item, like tools. */
  color?: string | null;
  defaultMode: ChatAgentRunMode;
  scope: ChatSkillScope;
};

const REFETCH_ALL = {
  refetchQueries: ["MySpawnableChatAgents", "ChatAgentsByScope"],
  awaitRefetchQueries: true,
};

/** Human-readable summary of what an agent may touch. */
function toolSummary(tools: string[] | null): string {
  if (tools === null) return "inherits the spawning chat's tools";
  if (tools.length === 0) return "no tools";
  return tools.join(", ");
}

// ---------------------------------------------------------------------------

const AgentFormModal = ({
  open,
  title,
  initial,
  saving,
  onCancel,
  onSubmit,
  canUseOrgScope,
}: {
  open: boolean;
  title: string;
  initial?: Partial<AgentFormValues> & {
    tools?: string[];
    skillIds?: number[];
  };
  saving: boolean;
  onCancel: () => void;
  onSubmit: (
    values: AgentFormValues & { tools: string[]; skillIds: number[] }
  ) => void;
  canUseOrgScope: boolean;
}) => {
  const [form] = Form.useForm<AgentFormValues>();
  const { data: capsData, error: capsError } =
    useQuery<ChatAgentCapabilitiesQuery>(CHAT_AGENT_CAPABILITIES, {
      fetchPolicy: "cache-first",
    });
  const caps = capsData?.chatAgentCapabilities;
  const blockedTools = caps?.blockedTools ?? [];
  const toolGroups = useAgentAssignableToolGroups(blockedTools);
  const notOffered = caps?.notOfferedToAgents ?? [];
  const allowedModels: string[] = caps?.allowedModels ?? [];
  const supportsEffort = useModelSupportsEffort();
  const { data: registryData } = useQuery(LLM_MODELS, {
    fetchPolicy: "cache-first",
  });
  const registry = registryData?.llmModels ?? [];
  const allowedEfforts: ChatEffort[] = caps?.allowedEfforts ?? [];
  const allowedColors: string[] = caps?.allowedColors ?? [];
  const { data: skillsData } = useQuery(MY_ACTIVE_CHAT_SKILLS, {
    fetchPolicy: "cache-and-network",
  });
  const skillOptions: Array<{ id: number; name: string }> =
    skillsData?.myActiveChatSkills ?? [];

  const scope = Form.useWatch("scope", form);
  const mode = Form.useWatch("defaultMode", form);
  const watchedModel = Form.useWatch("model", form);
  const pickedModel = typeof watchedModel === "string" ? watchedModel : null;

  // `tools` and `skillIds` are held outside Form.Item deliberately: the shared
  // Form.Item coerces an absent field value to "" (Form.tsx `field.value ?? ""`)
  // and MUI's multiple Select throws on a non-array value. Patching the shared
  // Form would touch every form in the app, so these two fields are controlled
  // locally and merged back on submit.
  // New agents start on inherit (an empty list), so there is no curated
  // default to keep in step with what the author can actually see.
  // The generated type, now that codegen emits it — a hand-rolled shape here
  // would not fail to compile if the server renamed a field.
  const mcpBundles: McpToolBundleType[] = caps?.mcpToolBundles ?? [];

  const [tools, setTools] = useState<string[]>(initial?.tools ?? []);
  const [skillIds, setSkillIds] = useState<number[]>(initial?.skillIds ?? []);
  const [color, setColor] = useState<string | null>(initial?.color ?? null);

  /**
   * Whether the author is narrowing the agent's tools at all.
   *
   * Off by default, which stores null — "inherit whatever the chat that
   * spawns me can use". That is deliberately NOT the same as ticking every
   * box: an explicit list is a snapshot, so an agent created today would
   * never gain a tool added next year, while inherit tracks the chat.
   *
   * Editing an agent that already has a list opens with it on, so the
   * control reflects what is stored rather than resetting it.
   */
  const [limitTools, setLimitTools] = useState<boolean>(
    (initial?.tools?.length ?? 0) > 0
  );

  // A bundle reads as selected only when every one of its tools is held —
  // a partially-held bundle is the result of an earlier edit, and showing it
  // as on would misreport what the agent can reach.
  const selectedBundleKeys = useMemo(
    () =>
      mcpBundles
        .filter(
          b => b.tools.length > 0 && b.tools.every(t => tools.includes(t.name))
        )
        .map(b => b.key),
    [mcpBundles, tools]
  );
  // "Every group" is stored as Sirius MCP with NO bundle tool names, and is
  // resolved at run time — so it also picks up read tools added later. Any
  // named bundle tool means the author narrowed to specific groups instead.
  const allBundleToolNames = useMemo(
    () => new Set(mcpBundles.flatMap(b => b.tools.map(t => t.name))),
    [mcpBundles]
  );
  const allGroupsMode =
    tools.includes(ChatToolType.SiriusMcp) &&
    !tools.some(t => allBundleToolNames.has(t));

  const toggleBundle = (key: string, on: boolean) =>
    setTools(prev => {
      const namesOf = (k: string) =>
        mcpBundles.find(b => b.key === k)?.tools.map(t => t.name) ?? [];
      const withoutBundleNames = prev.filter(n => !allBundleToolNames.has(n));
      if (allGroupsMode && !on) {
        // Leaving "every group": hold every OTHER group explicitly.
        const rest = mcpBundles
          .filter(b => b.key !== key)
          .flatMap(b => namesOf(b.key));
        return [...withoutBundleNames, ...rest];
      }
      const next = on
        ? [...prev, ...namesOf(key).filter(n => !prev.includes(n))]
        : prev.filter(n => !namesOf(key).includes(n));
      // Every group ticked again collapses back to "every group", so the
      // agent regains tools added later instead of freezing today's list.
      const everyGroupHeld = mcpBundles.every(b =>
        b.tools.every(t => next.includes(t.name))
      );
      return everyGroupHeld ? withoutBundleNames : next;
    });

  // State is seeded from `initial` ONCE, when the form mounts, and never
  // re-synced. Callers pass `initial` as an object literal, so it is a new
  // object on every render of the list behind the form — and that list
  // re-renders whenever a fetch lands. Re-syncing on it wiped the author's
  // edits mid-form. Each open is a fresh mount instead (callers key it).

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText={saving ? "Saving…" : "Save"}
      confirmLoading={saving}
      width={720}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          isAutoInvocable: false,
          defaultMode: RUN_MODE.Ephemeral,
          scope: SCOPE.Personal,
          ...initial,
        }}
        onFinish={(values: AgentFormValues) =>
          onSubmit({ ...values, tools, skillIds, color })
        }
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[
            { required: true, message: "Name is required" },
            {
              pattern: /^[a-zA-Z0-9_-]+$/,
              message: "Letters, numbers, hyphens and underscores only",
            },
          ]}
          extra="The assistant spawns this agent by name."
        >
          <Input maxLength={255} data-testid="agent-name-input" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Description is required" }]}
          extra="Shown to the assistant so it knows when to reach for this agent."
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="directive"
          label="Directive"
          rules={[{ required: true, message: "Directive is required" }]}
          extra="The agent's system prompt. Replaces the generic sub-agent instructions entirely."
        >
          <Input.TextArea rows={10} />
        </Form.Item>

        <Form.Item
          label="Skills"
          extra="Skills this agent composes with. Their instructions are appended to its directive when it runs."
        >
          <Select
            mode="multiple"
            allowClear
            value={skillIds}
            onChange={(next: unknown) =>
              setSkillIds(Array.isArray(next) ? (next as number[]) : [])
            }
            data-testid="agent-skills-select"
          >
            {skillOptions.map(sk => (
              <Select.Option key={sk.id} value={sk.id}>
                {sk.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Tools"
          extra="By default this agent can use whatever the chat that spawns it can use. Narrow it when you want a more focused agent — the assistant can always pick fewer for a given task, never more."
        >
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: 12,
              cursor: "pointer",
              marginBottom: 6,
            }}
          >
            <Checkbox
              size="small"
              checked={limitTools}
              data-testid="agent-limit-tools"
              // Each of these sits inside a <label>. MUI renders the input
              // inside a span, so the label does not see the click as
              // targeting its own control and activates it a second time —
              // the toggle flips back on a single click. Same guard ToolsTab
              // already uses on its checkboxes.
              onClick={e => e.stopPropagation()}
              onChange={e => {
                const on = e.target.checked;
                setLimitTools(on);
                // Clearing back to inherit rather than leaving a stale list
                // behind: the checkbox is the whole statement of intent.
                if (!on) setTools([]);
              }}
            />
            Limit to specific tools
          </label>

          {/* Grouped exactly as the chat tools menu groups them, so the two
              selections read as one product. Tools a sub-agent cannot run
              (charts render into the parent's transcript, preferences rewrite
              the user's own settings) are not offered at all — the server
              drops them regardless, and offering a control that silently does
              nothing is worse than omitting it. */}
          {/* Fail closed: the blocked list and the Sirius groups both come
              from the policy. Without it the picker would offer every tool —
              including ones an agent can never hold — and no groups at all. */}
          {limitTools && !caps && (
            <Text
              type="secondary"
              style={{ fontSize: 12 }}
              data-testid="agent-tools-policy-unavailable"
            >
              {capsError
                ? `Could not load which tools agents may use (${capsError.message}). Try again shortly.`
                : "Loading which tools agents may use…"}
            </Text>
          )}
          {limitTools && caps && (
            <div data-testid="agent-tools-picker">
              {toolGroups.map(group => (
                <div key={group.key} style={{ marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {group.title}
                  </Text>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {group.tools.map(t => (
                      // The same styled card the MCP bundles use. These were on
                      // the native `title` attribute, which next to a real
                      // tooltip reads as having none: ~1s delay, unstyled, and
                      // nothing on touch. Atlas has a perfectly good
                      // description; it was the affordance that was missing.
                      <Tooltip
                        key={t.tool}
                        title={
                          <div style={TOOLTIP_SURFACE}>
                            <div style={{ fontWeight: 600 }}>{t.name}</div>
                            <div style={{ fontSize: 11, marginTop: 2 }}>
                              {t.description}
                            </div>
                          </div>
                        }
                      >
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={tools.includes(t.tool)}
                            data-testid={`agent-tool-${t.tool}`}
                            onClick={e => e.stopPropagation()}
                            onChange={e =>
                              setTools(prev =>
                                e.target.checked
                                  ? [...prev, t.tool]
                                  : // Unticking Sirius MCP drops its groups too;
                                    // leftover names would be a narrowing with
                                    // nothing to narrow.
                                    prev.filter(
                                      x =>
                                        x !== t.tool &&
                                        !(
                                          t.tool === ChatToolType.SiriusMcp &&
                                          allBundleToolNames.has(x)
                                        )
                                    )
                              )
                            }
                          />
                          {t.name}
                        </label>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ))}

              {/* Sirius MCP is one checkbox worth every data tool the platform
                has. The bundles narrow it: pick none and the agent gets the
                whole read surface, pick some and it gets exactly those.
                Published by the server (chatAgentCapabilities) rather than
                grouped here, so the client cannot drift from the policy.

                A bundle is expanded to its tool names on save, not stored as
                a bundle — otherwise a tool added to a bundle later would
                silently widen every agent already using it. */}
              {tools.includes(ChatToolType.SiriusMcp) &&
                mcpBundles.length > 0 && (
                  <div style={{ marginTop: 8 }} data-testid="agent-mcp-bundles">
                    <Text
                      type="secondary"
                      style={{ fontSize: 11 }}
                      data-testid="agent-mcp-bundles-summary"
                    >
                      {allGroupsMode
                        ? "Sirius MCP data · every group selected — includes read tools added later. Untick a group to narrow."
                        : `Sirius MCP data · ${selectedBundleKeys.length} of ${mcpBundles.length} groups — fixed when you save; tools added to a group later are not included.`}
                    </Text>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {mcpBundles.map(b => (
                        <Tooltip
                          key={b.key}
                          title={
                            // Names alone ("getPmeCashflows") say nothing about
                            // what is being granted, so each carries the tool's
                            // own one-line summary.
                            //
                            // The shared Tooltip strips its dark surface for any
                            // non-string title (antd overlayInnerStyle contract:
                            // a custom node owns its own card), so this paints
                            // one — otherwise the text floats see-through over
                            // the page.
                            <div style={TOOLTIP_SURFACE}>
                              <div style={{ marginBottom: 4, fontWeight: 600 }}>
                                {b.description}
                              </div>
                              {b.tools.map(t => (
                                <div
                                  key={t.name}
                                  style={{ fontSize: 11, marginTop: 2 }}
                                >
                                  <strong>{t.name}</strong> — {t.description}
                                </div>
                              ))}
                            </div>
                          }
                        >
                          <label
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              fontSize: 12,
                              cursor: "pointer",
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={
                                allGroupsMode ||
                                selectedBundleKeys.includes(b.key)
                              }
                              // The last group cannot be unticked: with none held
                              // the agent would read as "every group" again. To
                              // drop Sirius data entirely, untick Sirius MCP.
                              disabled={
                                !allGroupsMode &&
                                selectedBundleKeys.length === 1 &&
                                selectedBundleKeys[0] === b.key
                              }
                              data-testid={`agent-mcp-bundle-${b.key}`}
                              onClick={e => e.stopPropagation()}
                              onChange={e =>
                                toggleBundle(b.key, e.target.checked)
                              }
                            />
                            {b.title}
                          </label>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}

              {/* Said, not silently omitted: an author looking for email or
                navigation should learn it is withheld on purpose, and why.
                Served with the reasons, so this cannot drift from the rule. */}
              {(notOffered.length > 0 || blockedTools.length > 0) && (
                <div style={{ marginTop: 10 }} data-testid="agent-not-offered">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Not available to agents
                  </Text>
                  {notOffered.map(c => (
                    <div
                      key={c.key}
                      style={{ fontSize: 12, marginTop: 4 }}
                      data-testid={`agent-not-offered-${c.key}`}
                    >
                      <span style={{ fontWeight: 600 }}>{c.title}</span>
                      {" — "}
                      {c.description}
                      {c.tools.length > 0 && (
                        <div style={{ fontSize: 11, opacity: 0.7 }}>
                          {c.tools.map(t => t.name).join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                  {blockedTools.length > 0 && (
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      <span style={{ fontWeight: 600 }}>
                        Main assistant only
                      </span>
                      {
                        " — acts on your own session or its transcript, which a sub-agent does not have."
                      }
                      <div style={{ fontSize: 11, opacity: 0.7 }}>
                        {blockedTools.map(toolLabel).join(", ")}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="model"
          label="Model"
          extra={
            <>
              Leave empty and the assistant picks per run. Only models cleared
              for agents are listed. Benchmarks from{" "}
              <a
                href={BENCHMARK_SOURCE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Artificial Analysis
              </a>
              .
            </>
          }
        >
          <Select allowClear data-testid="agent-model-select">
            {/* Options come from the server. Hardcoding them is how this
                shipped sending "haiku" to a ChatModel enum that spells it
                "Haiku", and how a new model would go missing here. */}
            {allowedModels.map(m => {
              const row = registry.find(r => r.key === m);
              const stats = modelStats(row ?? {}, registry.filter(r => allowedModels.includes(r.key)));
              const blurb = [row?.description, stats].filter(Boolean).join(" · ");
              return (
                <Select.Option key={m} value={m}>
                  {blurb ? `${m} — ${blurb}` : m}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>

        {/* A model without an effort setting never gets one from the server,
            so offering the field there would be a control that does nothing. */}
        {supportsEffort(pickedModel) && (
          <Form.Item
            name="effort"
            label="Effort"
            extra="How hard the agent reasons. Leave empty for the model's default; when set, the assistant cannot override it for a run."
          >
            <Select allowClear data-testid="agent-effort-select">
              {allowedEfforts.map(e => (
                <Select.Option key={e} value={e}>
                  {EFFORT_BLURB[e] ? `${effortLabel(e)} — ${EFFORT_BLURB[e]}` : effortLabel(e)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* How the agent's replies are marked in the chat. Offered from the
            server's palette keys; "Auto" keeps the color hashed from the
            name, which is what every agent gets until someone picks one. */}
        <div style={{ marginBottom: 16 }} data-testid="agent-color-picker">
          <Text style={{ fontSize: 14, display: "block", marginBottom: 6 }}>
            Color in chat
          </Text>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ButtonBase
              aria-label="Automatic color"
              aria-pressed={color == null}
              data-testid="agent-color-auto"
              onClick={() => setColor(null)}
              sx={{
                px: 1,
                height: 24,
                borderRadius: "12px",
                fontSize: 12,
                border: "1px solid",
                borderColor: color == null ? "text.primary" : "divider",
              }}
            >
              Auto
            </ButtonBase>
            {allowedColors.map(key => (
              <ButtonBase
                key={key}
                aria-label={`${key} color`}
                aria-pressed={color === key}
                data-testid={`agent-color-${key}`}
                onClick={() => setColor(key)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: AGENT_PALETTE[key] ?? "action.disabled",
                  // A ring outside the swatch, so the selected color itself
                  // stays readable.
                  outline: color === key ? "2px solid" : "none",
                  outlineColor: "text.primary",
                  outlineOffset: "2px",
                }}
              />
            ))}
          </div>
        </div>

        <Form.Item
          name="isAutoInvocable"
          label="Let the assistant choose this agent"
          valuePropName="checked"
          extra="Off: only runs when someone asks for it by name with @. On: the assistant may reach for it when the work suits — which costs a separate model call, so leave it off unless the agent earns it."
        >
          <Switch data-testid="agent-auto-invoke-switch" />
        </Form.Item>

        <Form.Item
          name="defaultMode"
          label="Default run mode"
          extra="What the assistant does unless it decides otherwise — it can choose either mode per run."
        >
          <Select data-testid="agent-mode-select">
            <Select.Option value={RUN_MODE.Ephemeral}>
              Ephemeral — each run is discarded
            </Select.Option>
            <Select.Option value={RUN_MODE.Persistent}>
              Persistent — keeps a thread it can be returned to
            </Select.Option>
          </Select>
        </Form.Item>

        {mode === RUN_MODE.Persistent && (
          <Alert
            type="info"
            showIcon
            message="By default this agent remembers: each chat gets its own thread, so it can weigh new material against what it already said. The assistant can still run it one-off when memory would not help."
          />
        )}

        <Form.Item name="scope" label="Scope">
          <Select data-testid="agent-scope-select">
            <Select.Option value={SCOPE.Personal}>
              Personal — only you
            </Select.Option>
            {canUseOrgScope && (
              <Select.Option value={SCOPE.Organization}>
                Organization — everyone in your organization
              </Select.Option>
            )}
          </Select>
        </Form.Item>

        {scope === SCOPE.Organization && (
          <Alert
            type="warning"
            showIcon
            message="Everyone in your organization will be able to spawn this agent."
          />
        )}
      </Form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------

/**
 * Version history.
 *
 * Diffs the directive AND the behavior fields. A skill's history can get away
 * with diffing text alone because a skill IS its text; an agent's behavior is
 * also its tools, model, run mode and skill set — so a history view that showed
 * only the directive would render an identical diff for a change that silently
 * widened what the agent can touch.
 */
const AgentHistoryModal = ({
  agent,
  skillNames,
  onClose,
}: {
  agent: ChatAgent | null;
  skillNames: Map<number, string>;
  onClose: () => void;
}) => {
  const [selected, setSelected] = useState<AgentHistoryEntry | null>(null);

  const { data, loading } = useQuery<{ chatAgentHistory: AgentHistoryEntry[] }>(
    CHAT_AGENT_HISTORY,
    {
      variables: { id: agent?.id ?? 0 },
      skip: !agent,
    }
  );

  const history: AgentHistoryEntry[] = data?.chatAgentHistory ?? [];

  // Derived, not synchronised: default to the newest version until the user
  // picks one. Preselecting via an onCompleted callback would set state from a
  // render-phase callback and would also yank the user off whatever version
  // they were reading every time the query refetched.
  const shown = selected ?? history[0] ?? null;

  const renderBehaviour = (entry: AgentHistoryEntry) => {
    if (!agent) return null;
    const rows: Array<[string, string, string]> = [
      ["Tools", toolSummary(entry.tools), toolSummary(agent.tools)],
      ["Model", entry.model ?? "default", agent.model ?? "default"],
      ["Effort", entry.effort ?? "default", agent.effort ?? "default"],
      ["Run mode", entry.defaultMode, agent.defaultMode],
      [
        "Skills",
        (entry.skills ?? [])
          .map(s => skillNames.get(s.skillId) ?? `#${s.skillId}`)
          .join(", ") || "none",
        agent.skills.map(s => s.name).join(", ") || "none",
      ],
    ];
    return (
      <div style={{ marginBottom: 12 }} data-testid="agent-history-behavior">
        {rows.map(([label, was, now]) => {
          const changed = was !== now;
          return (
            <div
              key={label}
              style={{
                display: "flex",
                gap: 8,
                fontSize: 11,
                padding: "2px 0",
                background: changed ? "#fffbe6" : "transparent",
              }}
            >
              <Text style={{ fontSize: 11, width: 70, fontWeight: 500 }}>
                {label}
              </Text>
              <Text type="secondary" style={{ fontSize: 11, flex: 1 }}>
                {was}
              </Text>
              <Text style={{ fontSize: 11, flex: 1 }}>
                {changed ? `→ ${now}` : "unchanged"}
              </Text>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      title={agent ? `Version History — ${agent.name}` : "Version History"}
      open={!!agent}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
      afterClose={() => setSelected(null)}
    >
      {loading && <CircularProgress size={16} />}
      {!loading && history.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No version history yet. A version is recorded each time an agent is
          updated.
        </Text>
      )}
      {!loading && history.length > 0 && (
        <div style={{ display: "flex", gap: 16, minHeight: 400 }}>
          <div
            style={{
              width: 200,
              flexShrink: 0,
              borderRight: "1px solid #f0f0f0",
              paddingRight: 12,
            }}
          >
            <Text
              type="secondary"
              style={{ fontSize: 11, display: "block", marginBottom: 6 }}
            >
              {history.length} saved version{history.length !== 1 ? "s" : ""}
            </Text>
            <List
              size="small"
              dataSource={history}
              renderItem={(entry: AgentHistoryEntry) => (
                <List.Item
                  style={{
                    padding: "4px 6px",
                    cursor: "pointer",
                    borderRadius: 4,
                    background:
                      shown?.id === entry.id ? "#e6f4ff" : "transparent",
                  }}
                  onClick={() => setSelected(entry)}
                >
                  <div>
                    <Text style={{ fontSize: 12, fontWeight: 500 }}>
                      v{entry.version}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: 10, display: "block" }}
                    >
                      {new Date(entry.archivedAt).toLocaleString()}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: 10, display: "block" }}
                      ellipsis
                    >
                      {entry.archivedBy}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          </div>
          <div style={{ flex: 1, overflow: "auto" }}>
            {shown && agent ? (
              <>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: "block", marginBottom: 8 }}
                >
                  Comparing v{shown.version} (left) to current (right)
                </Text>
                {renderBehaviour(shown)}
                <ReactDiffViewer
                  oldValue={shown.directive}
                  newValue={agent.directive}
                  splitView
                  compareMethod={DiffMethod.WORDS}
                  leftTitle={`v${shown.version}`}
                  rightTitle="Current"
                  styles={{
                    contentText: { fontFamily: "monospace", fontSize: "11px" },
                  }}
                />
              </>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Select a version to compare.
              </Text>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

// ---------------------------------------------------------------------------

const AgentRow = ({
  agent,
  onEdit,
  onDelete,
  onHistory,
  onFork,
}: {
  agent: ChatAgent;
  onEdit?: (a: ChatAgent) => void;
  onDelete?: (a: ChatAgent) => void;
  onHistory?: (a: ChatAgent) => void;
  onFork?: (a: ChatAgent) => void;
}) => (
  <List.Item key={agent.id} style={{ padding: "4px 0" }}>
    <div
      style={{
        display: "flex",
        width: "100%",
        gap: 8,
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            gap: 4,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <ScopeIcon scope={agent.scope} />
          <span
            aria-hidden
            data-testid={`agent-color-dot-${agent.id}`}
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              marginRight: 6,
              // The color the agent is drawn in, picked or automatic.
              backgroundColor: speakerTint(agent.name, agent.color).accent,
            }}
          />
          <Text strong>{agentDisplayName(agent.name)}</Text>
          {/* The handle is what people type, so it stays visible. */}
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
            @{agent.name}
          </Text>
          {agent.isAutoInvocable && (
            <Tooltip title="The assistant may choose this agent on its own, without being asked for by name">
              <span
                data-testid="agent-auto-invocable"
                style={{ display: "inline-flex" }}
              >
                <AutoAwesomeOutlined
                  style={{ fontSize: 14, color: "#ed6c02" }}
                />
              </span>
            </Tooltip>
          )}
          {agent.defaultMode === RUN_MODE.Persistent && (
            <Tooltip title="Remembers — keeps a thread per chat, so it can weigh new material against what it already said">
              <span
                data-testid="agent-persistent"
                style={{ display: "inline-flex" }}
              >
                <PsychologyOutlined
                  style={{ fontSize: 14, color: "#7b1fa2" }}
                />
              </span>
            </Tooltip>
          )}
          {agent.model && <Tag>{agent.model}</Tag>}
          {agent.effort && <Tag>{agent.effort} effort</Tag>}
          <Tooltip title={toolSummary(agent.tools)}>
            {/* Inheritance is a state, not a quantity, so it gets an icon like
                scope and persistence do. A bounded agent keeps its NUMBER —
                "how many can this touch" is the thing worth reading at a
                glance, and an icon cannot say it. */}
            <span
              data-testid={
                agent.tools === null
                  ? "agent-tools-inherit"
                  : "agent-tools-bounded"
              }
              style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
            >
              {agent.tools === null ? (
                <AllInclusive style={{ fontSize: 14, color: "#888" }} />
              ) : (
                <>
                  <HandymanOutlined style={{ fontSize: 13, color: "#888" }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {agent.tools.length}
                  </Text>
                </>
              )}
            </span>
          </Tooltip>
          {agent.skills.length > 0 && (
            <Tooltip
              title={`Composes with: ${agent.skills.map(s => s.name).join(", ")}`}
            >
              <span
                data-testid="agent-skills-count"
                style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
              >
                <BranchesOutlined style={{ fontSize: 13, color: "#1976d2" }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {agent.skills.length}
                </Text>
              </span>
            </Tooltip>
          )}
          <Tag>v{agent.version}</Tag>
        </div>
        <Text
          type="secondary"
          style={{ fontSize: 12, display: "block" }}
          ellipsis
        >
          {agent.description}
        </Text>
        <Text type="secondary" style={{ fontSize: 10 }}>
          {agent.createUserFullName || agent.createUser}
        </Text>
      </div>
      {onFork && (
        <Tooltip title="Copy to my agents">
          <span>
            <BranchesOutlined
              style={{ fontSize: 14, color: "#999", cursor: "pointer" }}
              data-testid={`fork-agent-${agent.id}`}
              onClick={() => onFork(agent)}
            />
          </span>
        </Tooltip>
      )}
      {onHistory && (
        <Tooltip title="Version history">
          <span>
            <HistoryOutlined
              style={{ fontSize: 14, color: "#999", cursor: "pointer" }}
              data-testid={`history-agent-${agent.id}`}
              onClick={() => onHistory(agent)}
            />
          </span>
        </Tooltip>
      )}
      {onEdit && agent.canEdit && (
        <Tooltip title="Edit">
          <span>
            <EditOutlined
              style={{ fontSize: 14, color: "#999", cursor: "pointer" }}
              data-testid={`edit-agent-${agent.id}`}
              onClick={() => onEdit(agent)}
            />
          </span>
        </Tooltip>
      )}
      {onDelete && agent.canDelete && (
        <Popconfirm
          title={`Delete "${agentDisplayName(agent.name)}"?`}
          description={
            agent.scope === SCOPE.Organization
              ? "This removes it for everyone in your organization."
              : undefined
          }
          onConfirm={() => onDelete(agent)}
          okText="Delete"
          cancelText="Cancel"
        >
          <DeleteOutlined
            style={{ fontSize: 14, color: "#999", cursor: "pointer" }}
            data-testid={`delete-agent-${agent.id}`}
          />
        </Popconfirm>
      )}
    </div>
  </List.Item>
);

// ---------------------------------------------------------------------------

function useAgentMutations() {
  const [createAgent, { loading: creating }] = useMutation(
    CREATE_CHAT_AGENT,
    REFETCH_ALL
  );
  const [updateAgent, { loading: updating }] = useMutation(
    UPDATE_CHAT_AGENT,
    REFETCH_ALL
  );
  const [deleteAgent] = useMutation(DELETE_CHAT_AGENT, REFETCH_ALL);
  return { createAgent, updateAgent, deleteAgent, creating, updating };
}

/**
 * Build the mutation input field by field.
 *
 * Deliberately NOT a rest-spread of form state: types are erased at runtime, so
 * a spread ships whatever the form happens to hold and the server rejects the
 * whole mutation (checklist item 12, incident GRAPHQL-13F).
 *
 * `tools` sends an explicit null for an empty selection rather than omitting
 * the field. On create the two are equivalent, but update treats an omitted
 * field as "leave unchanged" — so clearing every tool on an existing agent
 * silently did nothing while the UI reported success and the help text
 * promised it would inherit.
 */
function buildInput(
  values: AgentFormValues & { tools: string[]; skillIds: number[] },
  supportsEffort: (key: string | null | undefined) => boolean
) {
  return {
    name: values.name,
    description: values.description,
    directive: values.directive,
    isAutoInvocable: !!values.isAutoInvocable,
    tools: values.tools.length > 0 ? values.tools : null,
    model: values.model || undefined,
    // null, not undefined: clearing the field on an edit must reset the agent
    // to the model default rather than leave the old level in place. Dropped
    // for a model without an effort setting.
    effort:
      values.effort && supportsEffort(values.model) ? values.effort : null,
    // null, not undefined: choosing Auto on an edit must clear a picked color.
    color: values.color ?? null,
    defaultMode: values.defaultMode,
    scope: values.scope,
    skillIds: values.skillIds,
  };
}

/**
 * Run a mutation, report the outcome, and say whether it worked.
 *
 * The boolean is the point. This used to return void, so every caller closed
 * its modal unconditionally on the next line — a rejected save (a duplicate
 * name is the easy one to hit) showed an error toast and then destroyed the
 * form, taking a directive that can run to several hundred words with it.
 * Callers gate the close on the result, so a failed save leaves the form up
 * with the user's text still in it.
 */
export async function runMutation(
  fn: () => Promise<unknown>,
  ok: string
): Promise<boolean> {
  try {
    await fn();
    notify({ severity: "success", message: ok });
    return true;
  } catch (error) {
    notify({ severity: "error", message: extractGqlMessage(error) });
    return false;
  }
}

const MyAgentsList = ({ canUseOrgScope }: { canUseOrgScope: boolean }) => {
  const supportsEffort = useModelSupportsEffort();
  const currentUser = useOptionalCurrentUser();
  const { data, loading } = useQuery<{ mySpawnableChatAgents: ChatAgent[] }>(
    MY_SPAWNABLE_CHAT_AGENTS,
    {
      fetchPolicy: "cache-and-network",
    }
  );
  const { createAgent, updateAgent, deleteAgent, creating, updating } =
    useAgentMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ChatAgent | null>(null);
  const [history, setHistory] = useState<ChatAgent | null>(null);

  const all: ChatAgent[] = data?.mySpawnableChatAgents ?? [];
  // "My" agents are the ones this user authored, mirroring My Skills.
  const mine = all.filter(
    a => a.scope !== SCOPE.System && a.createUser === currentUser?.email
  );

  const skillNames = new Map<number, string>(
    all.flatMap(a => a.skills.map(s => [s.id, s.name] as [number, string]))
  );

  if (loading && all.length === 0) return <CircularProgress size={20} />;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>
          {mine.length} agent{mine.length !== 1 ? "s" : ""}
        </Text>
        <Button
          size="small"
          type="text"
          icon={<PlusOutlined />}
          onClick={() => setFormOpen(true)}
          data-testid="new-agent-button"
        >
          New
        </Button>
      </div>

      {mine.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No agents yet. An agent is a specialist the assistant can hand work to
          — its own directive, its own tools, its own context.
        </Text>
      )}

      <List
        size="small"
        dataSource={mine}
        renderItem={(agent: ChatAgent) => (
          <AgentRow
            agent={agent}
            onEdit={setEditing}
            onHistory={setHistory}
            onDelete={a =>
              runMutation(
                () => deleteAgent({ variables: { id: a.id } }),
                "Agent deleted"
              )
            }
          />
        )}
      />

      {/* Mounted per open, so a reopened form starts clean rather than
          carrying the last draft's tool state. */}
      {formOpen && (
        <AgentFormModal
          open
          title="New agent"
          saving={creating}
          canUseOrgScope={canUseOrgScope}
          onCancel={() => setFormOpen(false)}
          onSubmit={async values => {
            const saved = await runMutation(
              () =>
                createAgent({
                  variables: { input: buildInput(values, supportsEffort) },
                }),
              "Agent created"
            );
            if (saved) setFormOpen(false);
          }}
        />
      )}

      {editing && (
        <AgentFormModal
          key={`edit-${editing.id}`}
          open
          title={`Edit ${editing.name}`}
          saving={updating}
          canUseOrgScope={canUseOrgScope}
          initial={{
            name: editing.name,
            description: editing.description,
            directive: editing.directive,
            isAutoInvocable: editing.isAutoInvocable,
            model: editing.model ?? undefined,
            effort: editing.effort ?? undefined,
            color: editing.color,
            defaultMode: editing.defaultMode,
            scope: editing.scope,
            tools: editing.tools ?? [],
            skillIds: editing.skills.map(s => s.id),
          }}
          onCancel={() => setEditing(null)}
          onSubmit={async values => {
            const saved = await runMutation(
              () =>
                updateAgent({
                  variables: {
                    id: editing.id,
                    input: buildInput(values, supportsEffort),
                  },
                }),
              "Agent updated"
            );
            if (saved) setEditing(null);
          }}
        />
      )}

      <AgentHistoryModal
        agent={history}
        skillNames={skillNames}
        onClose={() => setHistory(null)}
      />
    </div>
  );
};

const ScopedAgentsList = ({
  scope,
  emptyMessage,
  canUseOrgScope,
}: {
  scope: ChatSkillScope;
  emptyMessage: string;
  canUseOrgScope: boolean;
}) => {
  const supportsEffort = useModelSupportsEffort();
  const currentUser = useOptionalCurrentUser();
  const organizationId = currentUser?.organizationId;
  const isOrg = scope === SCOPE.Organization;

  const { data, loading } = useQuery<{ chatAgentsByScope: ChatAgent[] }>(
    CHAT_AGENTS_BY_SCOPE,
    {
      variables: { scope, organizationId: isOrg ? organizationId : null },
      skip: isOrg && !organizationId,
      fetchPolicy: "cache-and-network",
    }
  );
  const { createAgent, updateAgent, deleteAgent, creating, updating } =
    useAgentMutations();

  const [forking, setForking] = useState<ChatAgent | null>(null);
  const [editing, setEditing] = useState<ChatAgent | null>(null);
  const [history, setHistory] = useState<ChatAgent | null>(null);

  const agents: ChatAgent[] = data?.chatAgentsByScope ?? [];
  const skillNames = new Map<number, string>(
    agents.flatMap(a => a.skills.map(s => [s.id, s.name] as [number, string]))
  );

  if (loading && agents.length === 0) return <CircularProgress size={20} />;
  if (agents.length === 0)
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {emptyMessage}
      </Text>
    );

  return (
    <div>
      <List
        size="small"
        dataSource={agents}
        renderItem={(agent: ChatAgent) => (
          <AgentRow
            agent={agent}
            onFork={setForking}
            onHistory={setHistory}
            onEdit={setEditing}
            onDelete={a =>
              runMutation(
                () => deleteAgent({ variables: { id: a.id } }),
                "Agent deleted"
              )
            }
          />
        )}
      />

      {forking && (
        <AgentFormModal
          key={`fork-${forking.id}`}
          open
          title={`Copy ${forking.name} to my agents`}
          saving={creating}
          canUseOrgScope={canUseOrgScope}
          initial={{
            name: `${forking.name}-copy`,
            description: forking.description,
            directive: forking.directive,
            isAutoInvocable: forking.isAutoInvocable,
            model: forking.model ?? undefined,
            effort: forking.effort ?? undefined,
            color: forking.color,
            defaultMode: forking.defaultMode,
            // A fork always lands in the caller's own space; copying an org or
            // system agent must not silently re-publish it at that scope.
            scope: SCOPE.Personal,
            tools: forking.tools ?? [],
            skillIds: forking.skills.map(s => s.id),
          }}
          onCancel={() => setForking(null)}
          onSubmit={async values => {
            const saved = await runMutation(
              () =>
                createAgent({
                  variables: { input: buildInput(values, supportsEffort) },
                }),
              "Agent copied"
            );
            if (saved) setForking(null);
          }}
        />
      )}

      {editing && (
        <AgentFormModal
          key={`edit-${editing.id}`}
          open
          title={`Edit ${editing.name}`}
          saving={updating}
          canUseOrgScope={canUseOrgScope}
          initial={{
            name: editing.name,
            description: editing.description,
            directive: editing.directive,
            isAutoInvocable: editing.isAutoInvocable,
            model: editing.model ?? undefined,
            effort: editing.effort ?? undefined,
            color: editing.color,
            defaultMode: editing.defaultMode,
            scope: editing.scope,
            tools: editing.tools ?? [],
            skillIds: editing.skills.map(s => s.id),
          }}
          onCancel={() => setEditing(null)}
          onSubmit={async values => {
            const saved = await runMutation(
              () =>
                updateAgent({
                  variables: {
                    id: editing.id,
                    input: buildInput(values, supportsEffort),
                  },
                }),
              "Agent updated"
            );
            if (saved) setEditing(null);
          }}
        />
      )}

      <AgentHistoryModal
        agent={history}
        skillNames={skillNames}
        onClose={() => setHistory(null)}
      />
    </div>
  );
};

export function AgentsTab() {
  const currentUser = useOptionalCurrentUser();
  // DC admins author organization agents. The server enforces this regardless
  // (ChatAgent.writeRule); this only keeps an option out of the picker that
  // could only ever be refused.
  const canUseOrgScope = !!currentUser?.flags?.DCAdmin;

  return (
    <>
      {/* The conceptual line lived only in the empty state, so it vanished as
          soon as you had one agent — exactly when you start wondering how an
          agent differs from a skill. It says what an agent IS, and points at
          the other thing, because "which of these do I reach for" is the
          actual question. */}
      <div style={TAB_INTRO_STYLE}>
        A <strong>specialist you can hand work to</strong>. It runs on its own
        instructions, tools and context, then reports back — so its answer is
        independent of the conversation that called it. Good for a second
        opinion, or a focused job you do not want crowding the chat. Reach for a{" "}
        <strong>skill</strong> instead when you want to shape how this
        conversation works.
      </div>
      <Tabs
        size="small"
        defaultActiveKey="my"
        items={[
          {
            key: "my",
            label: "My Agents",
            children: <MyAgentsList canUseOrgScope={canUseOrgScope} />,
          },
          {
            key: "org",
            label: "Org Agents",
            children: (
              <ScopedAgentsList
                scope={SCOPE.Organization}
                emptyMessage="No organization agents yet."
                canUseOrgScope={canUseOrgScope}
              />
            ),
          },
          {
            key: "system",
            label: "System",
            children: (
              <ScopedAgentsList
                scope={SCOPE.System}
                emptyMessage="No system agents yet."
                canUseOrgScope={canUseOrgScope}
              />
            ),
          },
        ]}
      />
    </>
  );
}
